import { useEffect, useState } from 'react';

/** Per-device Daaluu display scale, so small laptop screens can fit the whole table. Cosmetic only. */
export interface DaaluuDisplaySize { table: number; tiles: number }

export const DAALUU_SIZE_MIN = 0.5;
export const DAALUU_SIZE_MAX = 1;
const STORAGE_KEY = 'daaluu.displaySize';

const clampScale = (value: unknown): number => {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 1;
  return Math.round(Math.min(DAALUU_SIZE_MAX, Math.max(DAALUU_SIZE_MIN, n)) * 100) / 100;
};

/**
 * Size used until the player picks one: short windows (e.g. a 14" laptop with the Dock and toolbars)
 * start smaller so the dealing ring and seats fit without the player finding the setting.
 */
export function autoDaaluuSize(viewportHeight: number): DaaluuDisplaySize {
  if (viewportHeight < 760) return { table: 0.8, tiles: 0.75 };
  if (viewportHeight < 900) return { table: 0.9, tiles: 0.85 };
  return { table: 1, tiles: 1 };
}

function readSaved(): DaaluuDisplaySize | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<DaaluuDisplaySize> | null;
    return saved ? { table: clampScale(saved.table), tiles: clampScale(saved.tiles) } : null;
  } catch {
    return null;
  }
}

/**
 * The player's chosen size, or an automatic one from the window height. Publishes the raw values as
 * CSS variables on the document root while mounted (tile flights and drag ghosts are portaled).
 */
export function useDaaluuDisplaySize() {
  const [saved, setSaved] = useState<DaaluuDisplaySize | null>(readSaved);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  const size = saved ?? autoDaaluuSize(viewportHeight);

  useEffect(() => {
    if (saved) return;
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [saved]);

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--daaluu-table', String(size.table));
    root.setProperty('--daaluu-tiles', String(size.tiles));
    return () => {
      root.removeProperty('--daaluu-table');
      root.removeProperty('--daaluu-tiles');
    };
  }, [size.table, size.tiles]);

  const update = (next: Partial<DaaluuDisplaySize>) => {
    const chosen = { table: clampScale(next.table ?? size.table), tiles: clampScale(next.tiles ?? size.tiles) };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(chosen)); } catch { /* private mode */ }
    setSaved(chosen);
  };
  const reset = () => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
    setSaved(null);
  };
  return { size, isAuto: saved === null, update, reset };
}
